import useShowPropertyType from "@hooks/useShowPropertyType";
import { getCurrentTimezoneTimestamp } from "@src/utils/timezone";

import SettingsContext from "@context/Settings/settingsContext";
import ShowErrorToastContext from "@context/showErrorToast/showErrorToastContext";
import ShowSuccessToastContext from "@src/context/ShowSuccessToast/showSuccessToastContext";
import ConfirmationDialog from "@src/components/shared-ui-components/ConfirmationDialog";
import { Divider } from "@mui/material";
import LastUpdated from "@shared-containers/LastUpdated";
import { message } from "@src/constants";
import LocationContext from "@src/context/location/locationContext";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEVICE, useWindowSizeChanged } from "@src/hooks/DeviceSize";
import useShowNote from "@src/hooks/useShowNote";
import useNoteSectionVisibility from "@src/hooks/useNoteSectionVisibility";
import {
  LABOUR_COGS_TITLE,
  NOTES_TITLE,
  createNote,
  getHomePageData,
  getNoteData,
  updateNote,
  sendEmailsRequest,
} from "../helper";

import LabourCogs from "./LabourCogs";
import TotalSales from "./TotalSales";
import {
  getMappedDashboardData,
  initialDashboardData,
  PROPERTY,
} from "./TotalSales/helper";
import { CustomCardWrapper, DividerWrapper, LastUpdatedWrapper } from "./style";
import Notes from "./Notes";

const HomeDashboard = () => {
  const [noteViewMode, setNoteViewMode] = useState(true);
  const [{ locationState }] = useContext(LocationContext);

  const [{ showErrorToastSetState }] = useContext(ShowErrorToastContext);
  const [{ showSuccessToastSetState }] = useContext(ShowSuccessToastContext);

  const [{ settingsState }] = useContext(SettingsContext);

  const { planData, foreCastData } = settingsState;

  const { property, store, revenueCenter, date } = locationState;

  const [dashboardData, setdashboardData] =
    useState<dashboardDataTypes>(initialDashboardData);
  const [loading, setLoading] = useState<boolean>(false);
  const [notesLoading, setNotesLoading] = useState<boolean>(false);
  const [note, setNote] = useState<noteType | null>(null);
  const [
    isSendEmailConfirmationDialogOpen,
    setIsSendEmailConfirmationDialogOpen,
  ] = useState<boolean>(false);

  const quickServiceRevenueCenters = useShowPropertyType(
    dashboardData?.propertyType,
    "quickServiceRevenueCenters"
  );

  const propertyRestaurantCateringRevenueCenters = useShowPropertyType(
    dashboardData?.propertyType,
    "propertyRestaurantCateringRevenueCenters"
  );

  const propertyPinnacle = useShowPropertyType(
    dashboardData?.propertyType,
    "propertyPinnacle"
  );

  const displayNotesSection = useShowNote(
    locationState?.property,
    locationState?.store
  );

  const notesSectionVisibility = useNoteSectionVisibility(
    locationState?.property
  );

  const mappedDashboardData = getMappedDashboardData({
    mapData: dashboardData,
  });

  const device = useWindowSizeChanged();
  const isMobile = device === DEVICE.MOBILE;

  const getNote = async (query: NotesQueryType) => {
    setNotesLoading(true);
    const res = await getNoteData(query);
    if (!res.error) {
      setNote(res?.data || null);
    }
    setNotesLoading(false);
  };

  const allowStoreNotes = useMemo(() => {
    return Boolean(property === PROPERTY.RESTAURANTS_CATERING && store);
  }, [property, store]);

  const getData = async (filters: DashboardFilterType) => {
    const res = await getHomePageData(filters);
    setNote(null);
    setNoteViewMode(true);
    if (!res.error) {
      setdashboardData(res?.data);
      const query: NotesQueryType = { property, date };
      if (allowStoreNotes) {
        query.store = store;
      }
      if (displayNotesSection) getNote(query);
    } else {
      showErrorToastSetState({
        errorMessage: res?.error || "",
        errorTitle: message.ERROR_MESSAGE_DASHBOARD,
        open: true,
      });
    }
    setLoading(false);
  };

  const handleNoteCardClick = useCallback(() => {
    if (noteViewMode && !isMobile) setNoteViewMode(false);
  }, [isMobile, noteViewMode]);

  const onNoteUpdate = useCallback(
    async (content: string, sectionId: number, entryId: number) => {
      if (!note) return;
      setNotesLoading(true);

      const processedContent = content.replace(/<p><\/p>/g, "<br>");
      const res = await updateNote({
        content: processedContent.trim(),
        sectionId,
        entryId,
        noteId: note.noteId,
        timestamp: getCurrentTimezoneTimestamp(), // Add client timestamp
      });
      if (!res.error) {
        const query: NotesQueryType = { property, date };
        if (allowStoreNotes) {
          query.store = store;
        }
        getNote(query);
        showSuccessToastSetState({
          successMessage: message.SUCCESS_MESSAGE_NOTE_UPDATE,
          open: true,
        });
      } else {
        showErrorToastSetState({
          errorMessage: res?.error || "",
          errorTitle: message.ERROR_MESSAGE_NOTE,
          open: true,
        });
      }
      setNotesLoading(false);
    },
    [
      note,
      property,
      date,
      allowStoreNotes,
      store,
      showSuccessToastSetState,
      showErrorToastSetState,
    ]
  );

  const onNewNote = async () => {
    setNotesLoading(true);

    const body: CreateNoteType = {
      propertyId: property,
      date,
    };

    if (allowStoreNotes) {
      body.storeId = store;
    }

    const res = await createNote(body);
    if (!res.error) {
      const query: NotesQueryType = { property, date };
      if (allowStoreNotes) {
        query.store = store;
      }
      getNote(query);
      setNoteViewMode(false);
      showSuccessToastSetState({
        successMessage: message.SUCCESS_MESSAGE_NOTE_CREATE,
        open: true,
      });
    } else {
      showErrorToastSetState({
        errorMessage: res?.error || "",
        errorTitle: message.ERROR_MESSAGE_NOTE_CREATE,
        open: true,
      });
      setNotesLoading(false);
    }
  };

  const onConfirmSendNotesEmails = async () => {
    setIsSendEmailConfirmationDialogOpen(true);
  };

  const handleSendNotesConfirmation = async (confirmationState: boolean) => {
    if (note && confirmationState) {
      const res = await sendEmailsRequest(note.noteId as number);
      if (!res.error) {
        showSuccessToastSetState({
          successMessage: message.SUCCESS_MESSAGE_NOTE_EMAIL,
          open: true,
        });
      } else {
        showErrorToastSetState({
          successMessage: message.ERROR_MESSAGE_NOTE_EMAIL,
          open: true,
        });
      }
      setIsSendEmailConfirmationDialogOpen(false);
    } else {
      setIsSendEmailConfirmationDialogOpen(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    const filter: DashboardFilterType = {
      property,
      store,
      rvc: revenueCenter,
      date,
      planData,
      foreCastData,
    };
    getData(filter);
  }, [property, store, revenueCenter, date]);

  useEffect(() => {
    if (isMobile) setNoteViewMode(true);
  }, [isMobile]);

  // Clear dashboard data when location context changes significantly
  useEffect(() => {
    if (!property || !date) {
      setdashboardData(initialDashboardData);
      setNote(null);
    }
  }, [property, date]);

  // Force reset notes when location context changes to prevent tiptap editor issues
  const notesKey = useMemo(() => {
    return `${property}-${store}-${date}`;
  }, [property, store, date]);

  // Don't render dashboard content until context is loaded
  if (!property || !date) {
    return (
      <CustomCardWrapper>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          Loading Dashboard...
        </div>
      </CustomCardWrapper>
    );
  }

  return (
    <CustomCardWrapper>
      <TotalSales
        cardData={mappedDashboardData}
        filter={dashboardData?.propertyType}
        loading={loading}
      />
      {!(propertyRestaurantCateringRevenueCenters || propertyPinnacle) && (
        <>
          {!quickServiceRevenueCenters && (
            <LabourCogs
              LabourCogsData={dashboardData?.labour}
              title={LABOUR_COGS_TITLE.LABOUR}
              subTitle="Labour"
              loading={loading}
            />
          )}
          <LabourCogs
            LabourCogsData={dashboardData?.cogs}
            title={LABOUR_COGS_TITLE.COGS}
            subTitle="COGS"
            hideTarget={quickServiceRevenueCenters}
            loading={loading}
          />
        </>
      )}
      {displayNotesSection && (
        <Notes
          key={notesKey}
          data={note?.sections || null}
          title={NOTES_TITLE}
          loading={loading || notesLoading}
          viewMode={noteViewMode}
          isMobile={isMobile}
          notesSectionVisibility={notesSectionVisibility}
          onNewNote={onNewNote}
          onNoteClick={handleNoteCardClick}
          onNoteUpdate={onNoteUpdate}
          onConfirmSendNotesEmails={onConfirmSendNotesEmails}
          onDoneClick={() => {
            const query: NotesQueryType = { property, date };
            if (allowStoreNotes) {
              query.store = store;
            }
            setNote(null);
            getNote(query);
            setNoteViewMode(true);
          }}
        />
      )}
      <DividerWrapper>
        <Divider />
      </DividerWrapper>
      <LastUpdatedWrapper>
        <LastUpdated
          loading={loading}
          lastUpdatedTime={dashboardData?.last_updated}
          placeRight
        />
      </LastUpdatedWrapper>
      {isSendEmailConfirmationDialogOpen && (
        <ConfirmationDialog
          confirmationTitle={message.CONFIRMATION_TITLE_NOTE_EMAIL}
          confirmationMessage={message.CONFIRMATION_MESSAGE_NOTE_EMAIL}
          isOpen={isSendEmailConfirmationDialogOpen}
          onClose={handleSendNotesConfirmation}
        />
      )}
    </CustomCardWrapper>
  );
};

export default HomeDashboard;
